import { OrderSummaryEventQuery } from '@/src/graphql/graphql';
import { Box, Chip, ChipDelete, FormHelperText, Input, Stack } from '@mui/joy';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import sha256 from 'crypto-js/sha256';
import { StyledButton } from '../common/styled/ActionablesJoy.styled';
import { CartContext } from '../providers/cart';

interface PromoCodeInputProps {
    promoCodes: string[];
    setPromoCodes: (promoCode: string[]) => void;
    eventPromoCodes: NonNullable<OrderSummaryEventQuery['event']>['checkoutPromoCodes'];
    organizerPrimaryColor: string;
}

export default function PromoCodeInput({
    promoCodes,
    setPromoCodes,
    eventPromoCodes,
    organizerPrimaryColor,
}: PromoCodeInputProps) {
    const { t } = useTranslation();
    const [promoCode, setPromoCode] = useState<string>('');
    const [error, setError] = useState<string | null>(null);

    const { setCart } = useContext(CartContext);

    useEffect(() => {
        setCart({ promoCodes });
    }, [promoCodes, setCart]);

    const inputStyle = {
        width: '100%',
        '&:focus-within::before': {
            boxShadow: `inset 0px 0px 1px 1px ${organizerPrimaryColor}!important`,
        },
    };

    const handlePromoCodeApply = () => {
        setError(null);

        if (promoCode.length === 0 || promoCode.trim() === '') {
            setError(t('promoCode.errors.required', 'Promo code is required'));
            return;
        }

        const trimmedCode = promoCode.trim().toUpperCase();

        if (promoCodes.some((c) => c.toUpperCase() === trimmedCode)) {
            setError(t('promoCode.errors.alreadyApplied', 'Promo code already applied'));
            return;
        }

        const hashedPromoCode = sha256(trimmedCode).toString();

        const matchingPromoCode = eventPromoCodes?.find(
            (code) => code?.hashedPromoCode === hashedPromoCode
        );

        if (!matchingPromoCode) {
            setError(t('promoCode.errors.invalid', 'Invalid promo code'));
            return;
        }

        const now = new Date();

        if (new Date(matchingPromoCode.endDate) < now) {
            setError(t('promoCode.errors.expired', 'Promo code has expired'));
            return;
        }

        if (new Date(matchingPromoCode.startDate) > now) {
            setError(t('promoCode.errors.notYetActive', 'Promo code is not yet active'));
            return;
        }

        // Add promo code to the list
        const updatedPromoCodes = [...promoCodes, trimmedCode];
        setPromoCodes(updatedPromoCodes);

        // Clear the input
        setPromoCode('');
        setError(null);
    };

    const removePromoCode = (code: string) => {
        setPromoCodes(promoCodes.filter((c) => c !== code));
    };

    return (
        <Stack>
            <Box sx={{ display: 'flex', gap: 1, width: '100%', mb: 1 }}>
                <Input
                    value={promoCode}
                    placeholder={t('promoCode.placeholder', 'Enter promo code')}
                    id="promo-code"
                    name="promo-code"
                    disabled={promoCodes.length > 0}
                    onChange={(e) => setPromoCode(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            handlePromoCodeApply();
                        }
                    }}
                    sx={inputStyle}
                />
                <StyledButton
                    onClick={handlePromoCodeApply}
                    backgroundcolor={organizerPrimaryColor}
                    disabled={promoCodes.length > 0}
                >
                    {t('promoCode.apply', 'Apply')}
                </StyledButton>
            </Box>
            {error && <FormHelperText sx={{ color: 'red' }}>{error}</FormHelperText>}
            {promoCodes.map((code) => (
                <Chip
                    key={code}
                    size={'md'}
                    variant="soft"
                    color="neutral"
                    endDecorator={<ChipDelete onDelete={() => removePromoCode(code)} />}
                >
                    {code}
                </Chip>
            ))}
        </Stack>
    );
}
